import { faBookmark, faComments, faEnvelope, faPlus } from '@fortawesome/pro-regular-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { Box, Button, Checkbox, FormControlLabel, ListItemIcon, ListItemText, Menu, MenuItem } from '@vapor/material'
import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { EmailFragment, EmailState, useReservationUpdateMutation } from 'src/api/api.generated'
import { HiCIcon } from 'src/app/components/icons/icon'
import SendCommunicationEmail from 'src/app/components/send-communication-email/send-communication-email'
import useErrorSnackBar from 'src/app/hooks/error-snack-bar/error-snack-bar'
import useSuccessSnackBar from 'src/app/hooks/success-snack-bar/use-success-snack-bar'
import ChatDrawer from 'src/app/pages/common/mail-log/chat-drawer'
import MailLog, { ThreadRow } from 'src/app/pages/common/mail-log/mail-log-drawer'
import { MailLogRow } from 'src/app/pages/common/mail-log/mail-log-drawer'
import SendMessageTemplate from './send-message-template/send-message-template'

type MessagesTabProps = {
  reservationEmails: ReservationEmail[]
  reservationThread: ReservationThread[]
  loading: boolean
  propertyId: string
  tenantId: string
  reservationId: string
  disableAutomation: boolean
  guestEmail?: string
}

export type ReservationThread = {
  id: string
  lastMessageDate: Date | null
  lastMessage: string
  guestName: string
  archived: boolean
}

export type ReservationEmail = {
  id: string
  to: string
  subject: string
  emailState: EmailState
  body: string
  stateTransaction: {
    state: EmailState
    timestamp: Date
    result?: string | null | undefined
  }[]
}

export default function MessagesTab(props: MessagesTabProps) {
  const { reservationEmails, reservationThread, loading, propertyId, tenantId, reservationId, guestEmail, disableAutomation } = props
  const { t } = useTranslation()
  const [showMenu, setShowMenu] = useState<HTMLElement | null>(null)
  const [openSendMail, setOpenSendMail] = useState(false)
  const [openChat, setOpenChat] = useState(false)
  const [openSendMessageTemplate, setOpenSendMessageTemplate] = useState(false)
  const showSuccess = useSuccessSnackBar()
  const showError = useErrorSnackBar()

  const [updateReservation] = useReservationUpdateMutation({
    onError: showError,
  })

  function saveDisableAutomation() {
    updateReservation({
      variables: {
        id: reservationId,
        input: {
          tenantId,
          disableAutomation: !disableAutomation,
        },
      },
      onCompleted: () => {
        showSuccess(t('pms.booking.reservation.disable_automation_saved'))
      },
    })
  }

  function closeSendMail() {
    setOpenSendMail(false)
  }

  function closeSendMessageTemplate() {
    setOpenSendMessageTemplate(false)
  }

  const getLastMailState = (mail: EmailFragment) => {
    const descSortedEmailStates = [...mail.stateTransaction].sort((stateA, stateB) => {
      if (!stateA) return 1
      if (!stateB) return -1
      return stateB.timestamp.getTime() - stateA.timestamp.getTime()
    })
    return descSortedEmailStates[0]
  }

  const rows: (MailLogRow | ThreadRow)[] = useMemo(() => {
    const emails = reservationEmails.map((mail) => {
      const mailState = getLastMailState(mail)
      return {
        id: mail.id,
        date: mailState.timestamp,
        description: mail.subject,
        receiver: mail.to,
        state: mailState.state,
        template: mail.body,
        timeline: mail.stateTransaction,
        type: 'MAIL' as const,
      }
    })
    const threads = reservationThread.map((thread) => {
      return {
        id: thread.id,
        date: thread.lastMessageDate ?? new Date(),
        description: thread.lastMessage,
        receiver: thread.guestName,
        archived: thread.archived,
        type: 'THREAD' as const,
      }
    })

    return [...emails, ...threads].sort((a, b) => {
      return b.date.getTime() - a.date.getTime()
    })
  }, [reservationEmails.length, reservationThread.length])

  return (
    <>
      <Box display="flex" alignItems="center" justifyContent="space-between" marginBottom={2}>
        <FormControlLabel control={<Checkbox size="small" checked={disableAutomation} onChange={saveDisableAutomation} />} label={t('pms.booking.reservation_new.disable_automation')} />
        <Button
          variant="outlined"
          size="small"
          startIcon={<FontAwesomeIcon icon={faPlus} />}
          onClick={(event) => {
            setShowMenu(event.currentTarget)
          }}
        >
          {t('pms.messages.send_mail')}
        </Button>
      </Box>
      <Menu //
        open={showMenu !== null}
        anchorEl={showMenu}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'center',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'center',
        }}
        onClose={() => {
          setShowMenu(null)
        }}
      >
        <MenuItem
          onClick={() => {
            setOpenSendMessageTemplate(true)
          }}
        >
          <ListItemIcon>
            <HiCIcon>
              <FontAwesomeIcon icon={faBookmark} />
            </HiCIcon>
          </ListItemIcon>
          <ListItemText>{t('pms.messages.send_default_mail')}</ListItemText>
        </MenuItem>
        {reservationThread[0]?.id && (
          <MenuItem
            onClick={() => {
              setOpenChat(true)
            }}
          >
            <ListItemIcon>
              <HiCIcon>
                <FontAwesomeIcon icon={faComments} />
              </HiCIcon>
            </ListItemIcon>
            <ListItemText>{t('pms.mail_log.see_thread')}</ListItemText>
          </MenuItem>
        )}
        <MenuItem
          onClick={() => {
            setOpenSendMail(true)
          }}
        >
          <ListItemIcon>
            <HiCIcon>
              <FontAwesomeIcon icon={faEnvelope} />
            </HiCIcon>
          </ListItemIcon>
          <ListItemText>{t('pms.messages.write_mail')}</ListItemText>
        </MenuItem>
      </Menu>
      {reservationThread[0]?.id && openChat && (
        <ChatDrawer
          threadId={reservationThread[0]?.id}
          open={openChat}
          onClose={() => {
            setOpenChat(false)
          }}
        />
      )}
      <MailLog
        appearance={{
          type: 'RAW',
        }}
        toolbar={<></>}
        rows={rows}
        loading={loading}
      />
      {openSendMail && (
        <SendCommunicationEmail
          onClose={closeSendMail}
          onSendSuccess={closeSendMail}
          propertyId={propertyId}
          tenantId={tenantId}
          reservationId={reservationId}
          guestEmail={guestEmail}
          open={openSendMail}
        />
      )}
      {openSendMessageTemplate && (
        <SendMessageTemplate
          open={openSendMessageTemplate}
          onClose={closeSendMessageTemplate}
          onSendSuccess={closeSendMessageTemplate}
          propertyId={propertyId}
          tenantId={tenantId}
          reservationId={reservationId}
          guestEmail={guestEmail}
          reservationThread={reservationThread[0]?.id ?? undefined}
        />
      )}
    </>
  )
}
